import { CodeInput } from "@/components/code-input/CodeInput.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useEmployeeTwoFactorVerify } from "@/queries/employees/use-employees.query.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Stack, Title } from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface TwoFactorVerifyFormProps {
  accessToken: string;
  onSuccess: (newAccessToken: string) => void | Promise<void>;
}

export function TwoFactorVerifyForm({ accessToken, onSuccess }: TwoFactorVerifyFormProps) {
  const { t } = useTranslation();
  const twoFactorVerify = useEmployeeTwoFactorVerify();

  const verifySchema = z.object({
    code: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
  });

  type VerifySchema = z.infer<typeof verifySchema>;

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VerifySchema>({
    mode: "onSubmit",
    resolver: zodResolver(verifySchema),
  });

  const onSubmit = handleSubmit(async (formData) => {
    try {
      const data = await twoFactorVerify.mutateAsync({
        access_token: accessToken,
        code: formData.code,
      });

      if (data && data.access_token) {
        await onSuccess(data.access_token);
      }
    } catch {
      // Errors are handled by the mutation hook
    }
  });

  return (
    <Box className="w-full p-6 lg:p-8 bg-light rounded-xs shadow-md">
      <Stack gap={"2xl"}>
        <Stack gap={"lg"}>
          <Title
            header={t("core:two_factor")}
            size={"lg"}
            caption={t("core:enter_code_from_authenticator")}
          />

          <form onSubmit={onSubmit}>
            <Stack gap={"lg"}>
              <Controller
                control={control}
                name="code"
                render={({ field }) => (
                  <CodeInput
                    label={t("common:code")}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    error={errors.code?.message}
                    autoFocus
                    onComplete={() => onSubmit()}
                  />
                )}
              />

              <ServerErrorField errors={errors} />

              <Button
                variant={"secondary"}
                type={"submit"}
                size="lg"
                className="w-full"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                {t("common:verify")}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Stack>
    </Box>
  );
}
